import React, { useRef, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { BridgeDataItem } from './types';

interface WaterfallChartProps {
  bridgeData: BridgeDataItem[];
  sliderValue: number;
  googleChartsLoaded: boolean;
}

export const WaterfallChart: React.FC<WaterfallChartProps> = ({
  bridgeData,
  sliderValue,
  googleChartsLoaded
}) => {
  const chartRef = useRef<HTMLDivElement>(null);

  const drawChart = () => {
    if (!chartRef.current || !window.google || !window.google.visualization) return;
    const dataArray = [];
    let runningTotal = 0;
    const colors = bridgeData.map(item => item.fill);
    const startItem = bridgeData.find(item => item.isStart);
    if (startItem) {
      runningTotal = startItem.value;
      dataArray.push([startItem.name, 0, 0, runningTotal, runningTotal]);
    }
    bridgeData.forEach((item, index) => {
      if (!item.isStart && !item.isTarget && !item.isTotalBridged) {
        const prevTotal = runningTotal;
        runningTotal += item.value;
        dataArray.push([item.name, prevTotal, prevTotal, runningTotal, runningTotal]);
      }
    });
    const totalBridgedItem = bridgeData.find(item => item.isTotalBridged);
    if (totalBridgedItem) {
      dataArray.push([totalBridgedItem.name, 0, 0, totalBridgedItem.value, totalBridgedItem.value]);
    }
    const targetItem = bridgeData.find(item => item.isTarget);
    if (targetItem) {
      dataArray.push([targetItem.name, 0, 0, targetItem.value, targetItem.value]);
    }
    const data = new window.google.visualization.DataTable();
    data.addColumn('string', 'Category');
    data.addColumn('number', 'Start');
    data.addColumn('number', 'Start');
    data.addColumn('number', 'End');
    data.addColumn('number', 'End');
    data.addColumn({
      type: 'string',
      role: 'tooltip'
    });
    data.addColumn({
      type: 'string',
      role: 'style'
    });
    data.addRows(dataArray.map((row, i) => {
      const itemName = row[0];
      const item = bridgeData.find(b => b.name === itemName);
      let tooltip = '';
      let colorStyle = '';
      if (item) {
        tooltip = `${item.name}: ${item.displayValue}`;

        // Apply conditional coloring for non-special bars
        if (!item.isStart && !item.isTarget && !item.isTotalBridged) {
          // If the value is positive (increase), use green; if negative (decrease), use red
          const barColor = item.value >= 0 ? '#10B981' : '#ea384c';
          colorStyle = `color: ${barColor}`;
        } else {
          // Keep original colors for YTD Revenue, Total Bridged Revenue, and Target Revenue
          colorStyle = `color: ${item.fill}`;
        }
      }
      return [...row, tooltip, colorStyle];
    }));
    const ytdRevenueItem = bridgeData.find(item => item.isStart);
    const ytdRevenue = ytdRevenueItem?.value || 0;
    const minValue = ytdRevenue * (sliderValue / 100);
    const options = {
      title: 'Bridge Plan - Waterfall Chart',
      titleTextStyle: {
        color: '#ffffff',
        fontSize: 18,
        bold: true,
        fontName: 'Arial'
      },
      height: 400,
      legend: 'none',
      backgroundColor: {
        fill: 'transparent'
      },
      bar: {
        groupWidth: '85%'
      },
      candlestick: {
        fallingColor: {
          strokeWidth: 2,
          stroke: '#ffffff'
        },
        risingColor: {
          strokeWidth: 2,
          stroke: '#ffffff'
        }
      },
      annotations: {
        textStyle: {
          fontSize: 14,
          color: '#ffffff',
          bold: true,
          opacity: 1,
          fontName: 'Arial'
        },
        stem: {
          color: 'transparent',
          length: 20
        },
        alwaysOutside: true,
        highContrast: true
      },
      vAxis: {
        title: 'Revenue ($)',
        titleTextStyle: {
          color: '#ffffff',
          fontSize: 14,
          fontName: 'Arial',
          italic: false
        },
        textStyle: {
          color: '#ffffff',
          fontSize: 12,
          fontName: 'Arial'
        },
        format: 'currency',
        gridlines: {
          color: '#303339',
          count: 5
        },
        minorGridlines: {
          color: '#252830'
        },
        baselineColor: '#ffffff',
        viewWindow: {
          min: minValue
        }
      },
      hAxis: {
        textStyle: {
          color: '#ffffff',
          fontSize: 12,
          fontName: 'Arial',
          bold: true
        },
        slantedText: true,
        slantedTextAngle: 30
      },
      tooltip: {
        textStyle: {
          color: '#000000',
          fontSize: 12,
          fontName: 'Arial'
        },
        showColorCode: true,
        isHtml: true
      },
      animation: {
        startup: true,
        duration: 1000,
        easing: 'out'
      }
    };
    const chart = new window.google.visualization.CandlestickChart(chartRef.current);
    chart.draw(data, options);
    window.addEventListener('resize', () => {
      chart.draw(data, options);
    });
  };

  useEffect(() => {
    if (googleChartsLoaded && bridgeData.length > 0) {
      drawChart();
    }
  }, [bridgeData, sliderValue, googleChartsLoaded]);

  return (
    <Card className="bg-secondary/80 p-4 border border-primary/20 p-2 overflow-hidden flex-1 flex items-center justify-center rounded-none px-0 my-0 py-[15px]">
      <div className="h-[400px] w-full" ref={chartRef}></div>
    </Card>
  );
};
